import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Search, ChevronDown, Mail, ArrowUpRight } from "lucide-react";
import { searchFaqs } from "@/data/faqs";

const Result = ({ item, open, onToggle }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
    <button onClick={onToggle} data-testid={`widget-faq-${item.slug}`}
      className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left">
      <span className="text-[13px] text-white/85">{item.q}</span>
      <ChevronDown size={13} className={`shrink-0 text-cyan-300 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <div className="px-3.5 pb-3.5">
        <p className="text-xs leading-relaxed text-white/55">{item.a}</p>
        <Link to={`/help#${item.slug}`} data-testid={`widget-open-${item.slug}`}
          className="mt-2.5 inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200">
          Open in Help Center <ArrowUpRight size={11} />
        </Link>
      </div>
    )}
  </div>
);

export default function HelpWidget() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [openSlug, setOpenSlug] = useState(null);

  const searched = query.trim().length > 0;
  const results = useMemo(() => (searched ? searchFaqs(query).slice(0, 5) : []), [query, searched]);

  return (
    <div className="fixed bottom-5 right-5 z-50" data-testid="help-widget">
      {open && (
        <div className="mb-3 w-[min(360px,calc(100vw-2.5rem))] rounded-2xl border border-white/10 bg-[#0d0d14]/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden" data-testid="help-widget-panel">
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/[0.08] bg-white/[0.03]">
            <span className="text-sm font-500 text-white">ArcaCentum Help</span>
            <button onClick={() => setOpen(false)} data-testid="help-widget-close" aria-label="Close help"
              className="rounded-full p-1 text-white/40 hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>

          <div className="p-4">
            <div className="relative">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                autoFocus
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpenSlug(null); }}
                placeholder="Search help articles…"
                data-testid="help-widget-search"
                className="w-full rounded-full border border-white/10 bg-white/[0.04] pl-9 pr-4 py-2.5 text-[13px] text-white placeholder-white/35 outline-none focus:border-cyan-300/40 transition-colors"
              />
            </div>

            <div className="mt-3 max-h-[290px] overflow-y-auto space-y-2 pr-0.5">
              {!searched && (
                <p className="px-1 py-4 text-center text-xs text-white/45" data-testid="help-widget-hint">
                  Search for an answer first — most questions are already covered in our Help Center.
                </p>
              )}
              {searched && results.map((item) => (
                <Result key={item.slug} item={item} open={openSlug === item.slug} onToggle={() => setOpenSlug(openSlug === item.slug ? null : item.slug)} />
              ))}
              {searched && results.length === 0 && (
                <p className="px-1 py-4 text-center text-xs text-white/45" data-testid="help-widget-no-results">
                  No matching answers found.
                </p>
              )}
            </div>

            <div className="mt-3 border-t border-white/[0.08] pt-3 space-y-2">
              {searched ? (
                <a href={`mailto:support@arcacentum.com?subject=${encodeURIComponent("Support request: " + query.trim())}`}
                  data-testid="help-widget-email"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-4 py-2.5 text-[13px] text-white hover:bg-white/[0.1] transition-colors">
                  <Mail size={13} className="text-cyan-300" /> Still stuck? Email support
                </a>
              ) : (
                <p className="text-center text-[11px] text-white/35" data-testid="help-widget-email-gate">
                  Try a search above to unlock email support.
                </p>
              )}
              <Link to="/help" data-testid="help-widget-full-link"
                className="block text-center text-[11px] text-white/40 hover:text-white/70 transition-colors">
                Browse the full Help Center →
              </Link>
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setOpen((v) => !v)} data-testid="help-widget-button" aria-label="Help"
        className="ml-auto flex items-center gap-2 rounded-full bg-white px-4 py-3 text-sm font-500 text-[#0a0a0f] shadow-lg shadow-black/40 transition-transform duration-200 hover:scale-[1.04]">
        {open ? <X size={17} /> : <MessageCircle size={17} />}
        {!open && <span className="hidden sm:inline">Help</span>}
      </button>
    </div>
  );
}
